import React, { useState, useEffect, useRef } from 'react';
import { Mail, Search, RotateCw, Send, AlertCircle, Settings } from 'lucide-react';
import api from './api/axios';
import Pagination from './components/Pagination';

const EmailHistory = () => {
  // Stats state
  const [stats, setStats] = useState({ total_emails: 0, sent_today: 0, failed_count: 0, by_type: {} });

  // Table data state
  const [emailHistory, setEmailHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    total_records: 0,
    total_pages: 1,
    current_page: 1,
    page_size: 10,
    has_next: false,
    has_previous: false
  });

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [emailTypeFilter, setEmailTypeFilter] = useState('access_link,restore_link,add_accounts_link');
  const [daysFilter, setDaysFilter] = useState('');

  // Debounce ref for search
  const searchTimeoutRef = useRef(null);

  /**
   * Format an ISO date string to Eastern Time display.
   * @param {string} isoString - ISO 8601 date string
   * @returns {string} Formatted date in ET
   */
  const formatDateET = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }) + ' ET';
  };

  /**
   * Get display label and color classes for an email type.
   * @param {string} type - Email type key
   * @returns {{ label: string, classes: string }}
   */
  const getTypeBadge = (type) => {
    switch (type) {
      case 'access_link':
        return { label: 'Access Link', classes: 'bg-blue-100 text-blue-800' };
      case 'restore_link':
        return { label: 'Restore Link', classes: 'bg-orange-100 text-orange-800' };
      case 'add_accounts_link':
        return { label: 'Add Accounts Link', classes: 'bg-orange-100 text-orange-800' };
      case 'balance_alert':
        return { label: 'Balance Alert', classes: 'bg-purple-100 text-purple-800' };
      case 'account_removed':
        return { label: 'Account Removed', classes: 'bg-amber-100 text-amber-800' };
      case 'operating_low_balance':
        return { label: 'Operating Low Balance', classes: 'bg-rose-100 text-rose-800' };
      default:
        return { label: type || 'Unknown', classes: 'bg-gray-100 text-gray-800' };
    }
  };

  /**
   * Get display label and color classes for an email status.
   * @param {string} status - Email status
   * @returns {{ label: string, classes: string }}
   */
  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return { label: 'Sent', classes: 'bg-green-100 text-green-800' };
      case 'failed':
        return { label: 'Failed', classes: 'bg-red-100 text-red-800' };
      default:
        return { label: status || 'Unknown', classes: 'bg-gray-100 text-gray-800' };
    }
  };

  /**
   * Fetch paginated email history from the API.
   */
  const fetchEmailHistory = async (page = 1, size = pageSize, search = searchTerm, type = emailTypeFilter, days = daysFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: size });
      if (search) params.append('search', search);
      if (type) params.append('email_type', type);
      if (days) params.append('days', days);
      const response = await api.get(`/api/email-history/?${params}`);
      setEmailHistory(response.data.items);
      setPagination({
        total_records: response.data.total_records,
        total_pages: response.data.total_pages,
        current_page: response.data.current_page,
        page_size: response.data.page_size,
        has_next: response.data.has_next,
        has_previous: response.data.has_previous
      });
      setError(null);
    } catch (err) {
      console.error('Failed to load email history:', err);
      setError('Failed to load email history');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch summary stats for the stats cards.
   */
  const fetchStats = async () => {
    try {
      const response = await api.get('/api/email-history/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load email stats:', err);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchEmailHistory();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: fetchEmailHistory/fetchStats are redefined each render
  }, []);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchEmailHistory(page, pageSize, searchTerm, emailTypeFilter, daysFilter);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    fetchEmailHistory(1, size, searchTerm, emailTypeFilter, daysFilter);
  };

  // Search handler with debounce
  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchEmailHistory(1, pageSize, newSearchTerm, emailTypeFilter, daysFilter);
    }, 500);
  };

  // Filter change handlers
  const handleEmailTypeChange = (e) => {
    const value = e.target.value;
    setEmailTypeFilter(value);
    setCurrentPage(1);
    fetchEmailHistory(1, pageSize, searchTerm, value, daysFilter);
  };

  const handleDaysChange = (e) => {
    const value = e.target.value;
    setDaysFilter(value);
    setCurrentPage(1);
    fetchEmailHistory(1, pageSize, searchTerm, emailTypeFilter, value);
  };

  // Refresh handler
  const refreshData = () => {
    fetchEmailHistory(currentPage, pageSize, searchTerm, emailTypeFilter, daysFilter);
    fetchStats();
  };

  return (
    <div className="space-y-5">
      {/* Summary Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Total Emails Card */}
        <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-5">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">Total Emails</h2>
            <div className="p-2 bg-blue-50 rounded-full">
              <Mail className="w-6 h-6 text-blue-500" />
            </div>
          </div>
          <p className="text-3xl font-bold mt-4 text-gray-900 font-mono tabular-nums">{stats.total_emails}</p>
          <p className="text-sm text-gray-500 mt-1">All time</p>
        </div>

        {/* Sent Today Card */}
        <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-5">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">Sent Today</h2>
            <div className="p-2 bg-green-50 rounded-full">
              <Send className="w-6 h-6 text-green-500" />
            </div>
          </div>
          <p className="text-3xl font-bold mt-4 text-gray-900 font-mono tabular-nums">{stats.sent_today}</p>
          <p className="text-sm text-gray-500 mt-1">Emails sent today</p>
        </div>

        {/* Failed Card */}
        <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-5">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">Failed</h2>
            <div className="p-2 bg-red-50 rounded-full">
              <AlertCircle className="w-6 h-6 text-red-500" />
            </div>
          </div>
          <p className={`text-3xl font-bold mt-4 font-mono tabular-nums ${stats.failed_count > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {stats.failed_count}
          </p>
          <p className="text-sm text-gray-500 mt-1">Failed deliveries</p>
        </div>
      </div>

      {/* Email History Table */}
      <div className="bg-white rounded-3xl shadow-md border border-gray-100 overflow-hidden">
        {/* Table Header with Filters */}
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center">
            <h2 className="text-lg font-semibold text-gray-800">Email History</h2>
            <button
              onClick={refreshData}
              disabled={loading}
              className={`ml-3 p-1 rounded-full ${loading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-500 hover:bg-blue-100'}`}
              title="Refresh data"
            >
              <RotateCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            {/* Search Bar */}
            <div className="relative w-full sm:w-72">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by recipient, name, or subject"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {searchTerm && (
                <button
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => {
                    setSearchTerm('');
                    setCurrentPage(1);
                    fetchEmailHistory(1, pageSize, '', emailTypeFilter, daysFilter);
                  }}
                >
                  <span className="text-gray-400 hover:text-gray-500">&times;</span>
                </button>
              )}
            </div>

            {/* Email Type Filter */}
            <select
              value={emailTypeFilter}
              onChange={handleEmailTypeChange}
              className="px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="access_link,restore_link,add_accounts_link">Link Emails</option>
              <option value="">All Types</option>
              <option value="access_link">Access Link</option>
              <option value="restore_link">Restore Link</option>
              <option value="add_accounts_link">Add Accounts Link</option>
              <option value="balance_alert">Balance Alert</option>
              <option value="account_removed">Account Removed</option>
              <option value="operating_low_balance">Operating Low Balance</option>
            </select>

            {/* Date Range Filter */}
            <select
              value={daysFilter}
              onChange={handleDaysChange}
              className="px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Time</option>
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
            </select>
          </div>
        </div>

        {/* Search results indicator */}
        {searchTerm && (
          <div className="px-6 py-2 bg-gray-50 border-b border-gray-200 text-sm text-gray-500">
            Showing results for "{searchTerm}" - {pagination.total_records} {pagination.total_records === 1 ? 'email' : 'emails'} found
          </div>
        )}

        {/* Table Content */}
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-500">Loading email history...</span>
          </div>
        ) : error ? (
          <div className="p-4 bg-red-50 text-red-700 rounded-md m-4">
            {error}
          </div>
        ) : emailHistory.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-50 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500">No email history found</p>
            <p className="text-sm text-gray-400 mt-1">Emails will appear here once they are sent</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/80">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Date/Time
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Sent By
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {emailHistory.map((email) => {
                  const typeBadge = getTypeBadge(email.email_type);
                  const statusBadge = getStatusBadge(email.status);

                  return (
                    <tr key={email.id} className="hover:bg-blue-50/50">
                      {/* Date/Time */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDateET(email.created_at)}
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-medium rounded-full ${typeBadge.classes}`}>
                          {typeBadge.label}
                        </span>
                      </td>

                      {/* Recipient */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {email.recipient_name || '-'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {email.recipient_email}
                        </div>
                      </td>

                      {/* Subject */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900 truncate max-w-xs" title={email.subject}>
                          {email.subject}
                        </div>
                      </td>

                      {/* Sent By */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {email.sender && email.sender.full_name ? (
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-800">
                                {email.sender.full_name.split(' ').map(name => name[0]).join('')}
                              </span>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">{email.sender.full_name}</div>
                              <div className="text-xs text-gray-500">{email.sender.email}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            <Settings className="w-3 h-3 mr-1" />
                            System
                          </span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-medium rounded-full ${statusBadge.classes}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && !error && emailHistory.length > 0 && (
          <div className="px-6 py-4">
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSize={pageSize}
              pageSizeOptions={[10, 15, 20]}
              showInfo={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailHistory;
